import { useEffect, useMemo, useReducer, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Copy, RefreshCw, Send, Terminal, Trash2, X } from 'lucide-react';
import { useSocket } from '../../../contexts/SocketContext';
import type { CustomConsoleSummary } from '../../../types/custom';
import {
  CONSOLE_HISTORY_LIMIT,
  CONSOLE_PENDING_LIMIT,
  consoleCommandBytes,
  consoleCommandSuggestions,
  completeConsoleCommand,
  consoleDisplayRows,
  consoleLiteralText,
  consoleTranscriptText,
  customConsoleReducer,
  initialCustomConsoleState,
  isCustomConsoleEvent,
  type ConsoleEntry,
} from '../../../utils/customConsole';

interface CustomConsoleTarget {
  id: string;
  name: string;
  managementConsole?: CustomConsoleSummary;
}

const STATUS_LABELS = {
  pending: 'Waiting for result',
  completed: 'Completed',
  not_started: 'Not started',
  failed: 'Failed',
  unknown: 'Result unknown',
  error: 'Error',
};

type BrowserSocket = NonNullable<ReturnType<typeof useSocket>['socket']>;

function emitConsoleCommand(socket: BrowserSocket, payload: unknown) {
  // The workspace still includes legacy @types/socket.io-client; runtime is v4.8.
  const currentSocket = socket as BrowserSocket & { readonly volatile: BrowserSocket };
  currentSocket.volatile.emit('custom:console:command', payload);
}

export function CustomConsoleModal({ instance, onClose }: { instance: CustomConsoleTarget; onClose: () => void }) {
  const { socket, connected } = useSocket();
  const [state, dispatch] = useReducer(customConsoleReducer, initialCustomConsoleState);
  const [line, setLine] = useState('');
  const [selection, setSelection] = useState<{ start: number | null; end: number | null }>({ start: 0, end: 0 });
  const [inputFocused, setInputFocused] = useState(false);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const historyDraft = useRef('');
  const [following, setFollowing] = useState(true);
  const [retry, setRetry] = useState(0);
  const [feedback, setFeedback] = useState('');
  const sessionRef = useRef<string | null>(null);
  const ackRef = useRef('');
  const pendingTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const available = instance.managementConsole?.available === true;
  const unavailableReason = instance.managementConsole?.reason || 'This pinned revision does not declare a supported console.';
  const maxBytes = Math.min(instance.managementConsole?.capabilities?.maxCommandBytes || 4096, 4096);
  const bytes = consoleCommandBytes(line);
  const pending = state.entries.filter(entry => entry.kind === 'command' && entry.status === 'pending').length;
  const ready = available && connected && state.connection === 'connected';
  const canSubmit = ready && !!line.trim() && bytes <= maxBytes && pending < CONSOLE_PENDING_LIMIT;
  const suggestions = useMemo(() => ready && inputFocused && !suggestionsDismissed
    ? consoleCommandSuggestions(state.commands, line, selection.start, selection.end) : [],
  [ready, inputFocused, suggestionsDismissed, state.commands, line, selection.start, selection.end]);
  const activeSuggestion = Math.min(suggestionIndex, suggestions.length - 1);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const dialog = dialogRef.current;
    if (!overlay || !dialog) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const background = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay)
      .map(element => ({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') }));
    for (const item of background) {
      item.element.inert = true;
      item.element.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    const keydown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      } else if (event.key === 'Tab') {
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ));
        if (!focusable.length) {
          event.preventDefault();
          dialog.focus();
        } else if (event.shiftKey && document.activeElement === focusable[0]) {
          event.preventDefault();
          focusable[focusable.length - 1].focus();
        } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
          event.preventDefault();
          focusable[0].focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousOverflow;
      for (const item of background) {
        item.element.inert = item.inert;
        if (item.ariaHidden === null) item.element.removeAttribute('aria-hidden');
        else item.element.setAttribute('aria-hidden', item.ariaHidden);
      }
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    if (!socket || !connected || !available) {
      sessionRef.current = null;
      dispatch({ type: 'disconnect', message: available ? 'Connection to Hostinator lost. Waiting to reconnect.' : unavailableReason });
      return;
    }
    const sessionId = crypto.randomUUID();
    sessionRef.current = sessionId;
    ackRef.current = '';
    dispatch({ type: 'open', sessionId });
    const connectionTimer = setTimeout(() => {
      if (sessionRef.current !== sessionId) return;
      if (socket.connected) socket.emit('custom:console:close', { instanceId: instance.id, sessionId });
      sessionRef.current = null;
      dispatch({ type: 'event', sessionId, event: { type: 'state', state: 'error', message: 'Console connection timed out. Reconnect to try again.' } });
    }, 30_000);
    const receive = (payload: { instanceId?: string; sessionId?: string; event?: unknown } | null) => {
      if (!payload || payload.instanceId !== instance.id || payload.sessionId !== sessionId || sessionRef.current !== sessionId) return;
      const event = isCustomConsoleEvent(payload.event) ? payload.event : {
        type: 'error' as const, code: 'invalid_console_event', message: 'Unrecognized console message.',
      };
      if (event.type === 'state' && (event.state === 'connected' || event.state === 'error')) clearTimeout(connectionTimer);
      dispatch({ type: 'event', sessionId, event });
    };
    socket.on('custom:console:event', receive);
    socket.emit('custom:console:open', { instanceId: instance.id, sessionId });
    return () => {
      sessionRef.current = null;
      clearTimeout(connectionTimer);
      socket.off('custom:console:event', receive);
      // Never enqueue a close on a disconnected Socket.IO transport for its next connection.
      if (socket.connected) socket.emit('custom:console:close', { instanceId: instance.id, sessionId });
    };
  }, [socket, connected, available, unavailableReason, instance.id, retry]);

  // Acknowledge consumed log batches/gaps only after React commits their transcript update.
  // The protocol's subscription watermark is never exposed as a consumed cursor.
  useEffect(() => {
    const consumed = state.consumed;
    if (!consumed || !socket?.connected || !connected || consumed.sessionId !== sessionRef.current) return;
    const key = `${consumed.sessionId}:${consumed.version}`;
    if (ackRef.current === key) return;
    ackRef.current = key;
    socket.emit('custom:console:ack', { instanceId: instance.id, sessionId: consumed.sessionId, cursor: consumed.cursor });
  }, [state.consumed, socket, connected, instance.id]);

  useEffect(() => {
    const timers = pendingTimers.current;
    const pendingEntries = state.entries.filter(entry => entry.kind === 'command' && entry.status === 'pending');
    const pendingIds = new Set(pendingEntries.map(entry => entry.kind === 'command' ? entry.requestId : ''));
    for (const [requestId, timer] of timers) {
      if (!pendingIds.has(requestId)) { clearTimeout(timer); timers.delete(requestId); }
    }
    for (const entry of pendingEntries) {
      if (entry.kind !== 'command' || timers.has(entry.requestId)) continue;
      timers.set(entry.requestId, setTimeout(() => {
        timers.delete(entry.requestId);
        if (sessionRef.current === entry.sessionId) dispatch({ type: 'event', sessionId: entry.sessionId, event: {
          type: 'error', requestId: entry.requestId, code: 'console_result_unknown', execution: 'unknown',
          message: 'No result was received in time. The command may have executed; it was not sent again.',
        } });
      }, 20_000));
    }
  }, [state.entries]);

  useEffect(() => {
    const timers = pendingTimers.current;
    return () => { for (const timer of timers.values()) clearTimeout(timer); timers.clear(); };
  }, []);

  useEffect(() => {
    if (following && outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [state.entries, following]);

  useEffect(() => { if (ready) inputRef.current?.focus(); }, [ready]);

  useEffect(() => {
    suggestionsRef.current?.children[activeSuggestion]?.scrollIntoView({ block: 'nearest' });
  }, [activeSuggestion, suggestions]);

  function updateLine(value: string, start: number | null = value.length, end: number | null = start) {
    setLine(value);
    setSelection({ start, end });
    setSuggestionIndex(0);
    setSuggestionsDismissed(false);
  }

  function acceptSuggestion(index: number): boolean {
    const input = inputRef.current;
    const suggestion = suggestions[index];
    if (!input || !suggestion) return false;
    const completed = completeConsoleCommand(line, suggestion.name, input.selectionStart, input.selectionEnd);
    if (completed === null || consoleCommandBytes(completed) > maxBytes) return false;
    updateLine(completed);
    setHistoryIndex(null);
    setSuggestionsDismissed(true);
    input.focus();
    return true;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const sessionId = sessionRef.current;
    if (!canSubmit || !socket?.connected || !sessionId) return;
    const requestId = crypto.randomUUID();
    dispatch({ type: 'command', sessionId, requestId, line });
    // Check the transport immediately; Socket.IO must never buffer a user command for replay.
    emitConsoleCommand(socket, { instanceId: instance.id, sessionId, requestId, line });
    setHistory(previous => (previous[previous.length - 1] === line ? previous : [...previous, line]).slice(-CONSOLE_HISTORY_LIMIT));
    updateLine('');
    setHistoryIndex(null);
    historyDraft.current = '';
    setFeedback('');
  }

  function navigateHistory(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing) return;
    if (suggestions.length) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setSuggestionsDismissed(true);
        return;
      }
      if (event.key === 'Tab' && !event.shiftKey && acceptSuggestion(activeSuggestion)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        setSuggestionIndex((activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
        return;
      }
    }
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    if (!history.length) return;
    event.preventDefault();
    if (event.key === 'ArrowUp') {
      if (historyIndex === null) historyDraft.current = line;
      const next = historyIndex === null ? history.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(next);
      updateLine(history[next]);
    } else if (historyIndex !== null) {
      const next = historyIndex + 1;
      setHistoryIndex(next < history.length ? next : null);
      updateLine(next < history.length ? history[next] : historyDraft.current);
    }
    setSuggestionsDismissed(true);
  }

  async function copyOutput() {
    try {
      await navigator.clipboard.writeText(consoleTranscriptText(state.entries));
      setFeedback('Output copied.');
    } catch {
      setFeedback('Copy is unavailable. Select the output to copy it.');
    }
  }

  return createPortal(<div ref={overlayRef} className="modal-overlay z-[100]" onClick={onClose}>
    <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="custom-console-title"
      className="modal-content max-w-5xl" onClick={event => event.stopPropagation()}>
      <div className="modal-header">
        <div className="min-w-0"><h2 id="custom-console-title" className="modal-title flex items-center gap-2"><Terminal className="w-5 h-5 shrink-0 text-neon-400" /><span className="truncate">{instance.name} Console</span></h2>
          <p className="text-xs text-gray-500 mt-1">Commands and server output</p></div>
        <div className="flex items-center gap-2"><span role="status" className={ready ? 'badge-online' : (state.connection === 'error' ? 'badge-error' : 'badge-offline')}>{state.connection}</span>
          <button ref={closeRef} type="button" className="btn-ghost p-2" aria-label="Close console" onClick={onClose}><X className="w-4 h-4" /></button></div>
      </div>
      <div className="modal-body space-y-3">
        {state.message && <p className={`rounded border p-3 text-sm whitespace-pre-wrap ${state.connection === 'error' ? 'border-red-500/20 bg-red-500/10 text-red-300' : 'border-dark-600 text-gray-400'}`}>{consoleLiteralText(state.message)}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="text-xs text-gray-400 flex items-center gap-2"><input type="checkbox" checked={following} onChange={event => setFollowing(event.target.checked)} /> Follow output</label>
          <div className="flex gap-2"><button type="button" className="btn-ghost text-xs" disabled={!state.entries.length} onClick={() => void copyOutput()}><Copy className="w-3.5 h-3.5" /> Copy output</button>
            <button type="button" className="btn-ghost text-xs" disabled={!state.entries.length} onClick={() => dispatch({ type: 'clear' })}><Trash2 className="w-3.5 h-3.5" /> Clear completed</button>
            {(state.connection === 'error' || state.connection === 'disconnected') && <button type="button" className="btn-secondary text-xs" disabled={!available || !connected} onClick={() => setRetry(value => value + 1)}><RefreshCw className="w-3.5 h-3.5" /> Reconnect</button>}</div>
        </div>
        <div ref={outputRef} role="log" aria-label="Console output" aria-live="off" tabIndex={0}
          onScroll={event => {
            const element = event.currentTarget;
            setFollowing(element.scrollHeight - element.scrollTop - element.clientHeight < 40);
          }}
          className="h-[50vh] min-h-48 overflow-auto rounded-lg bg-black/60 border border-dark-600 p-4 font-mono text-xs leading-5 whitespace-pre-wrap break-words">
          {state.entries.length ? state.entries.map(entry => <TranscriptEntry key={entry.id} entry={entry} />)
            : <p className="text-gray-500">{ready ? 'Ready for a command.' : 'Waiting for the console connection…'}</p>}
        </div>
        <form onSubmit={submit} className="space-y-2">
          <div className="flex gap-2"><label htmlFor="custom-console-command" className="sr-only">Console command</label>
            <div className="relative min-w-0 flex-1">
              <input ref={inputRef} id="custom-console-command" role="combobox" className="input w-full font-mono text-sm" value={line}
                disabled={!ready} autoComplete="off" spellCheck={false} placeholder="Type a command…"
                aria-autocomplete="list" aria-expanded={suggestions.length > 0}
                aria-controls={suggestions.length ? 'custom-console-suggestions' : undefined}
                aria-activedescendant={suggestions.length ? `custom-console-suggestion-${activeSuggestion}` : undefined}
                aria-describedby="custom-console-command-help" aria-invalid={bytes > maxBytes}
                onFocus={() => { setInputFocused(true); setSuggestionsDismissed(false); }} onBlur={() => setInputFocused(false)}
                onSelect={event => setSelection({ start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd })}
                onKeyDown={navigateHistory} onChange={event => {
                  updateLine(event.target.value, event.target.selectionStart, event.target.selectionEnd);
                  setHistoryIndex(null);
                }} />
              {suggestions.length > 0 && <div ref={suggestionsRef} id="custom-console-suggestions" role="listbox" aria-label="Command suggestions"
                className="absolute bottom-full left-0 right-0 z-10 mb-2 max-h-[min(14rem,30vh)] overflow-y-auto rounded-lg border border-dark-500 bg-dark-800 shadow-xl">
                {suggestions.map((suggestion, index) => <div key={suggestion.name} id={`custom-console-suggestion-${index}`}
                  role="option" aria-selected={index === activeSuggestion}
                  className={`cursor-pointer px-3 py-2 text-xs break-words ${index === activeSuggestion ? 'bg-neon-400/10 text-neon-300' : 'text-gray-300 hover:bg-dark-700'}`}
                  onMouseDown={event => event.preventDefault()} onClick={() => acceptSuggestion(index)}>
                  <div className="font-mono whitespace-pre-wrap [overflow-wrap:anywhere]">{consoleLiteralText(suggestion.command?.usage || suggestion.name)}</div>
                  {suggestion.command && <div className="mt-1 text-gray-400 whitespace-pre-wrap [overflow-wrap:anywhere]">{consoleLiteralText(suggestion.command.description)}</div>}
                  {suggestion.hasChildren && <div className="mt-1 text-gray-500">Type . for subcommands</div>}
                </div>)}
              </div>}
            </div>
            <button type="submit" className="btn-primary" disabled={!canSubmit}><Send className="w-4 h-4" /> Send</button></div>
          <div id="custom-console-command-help" className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
            <span>{pending >= CONSOLE_PENDING_LIMIT ? 'Wait for a pending command to finish.' : suggestions.length
              ? '↑ / ↓ suggestions · Tab complete · Esc dismiss · Enter send'
              : `↑ / ↓ history${state.commands.length ? ' · Tab complete' : instance.managementConsole?.capabilities?.commandDiscovery ? ' · Type help to discover commands' : ''}`}</span>
            <span className={bytes > maxBytes ? 'text-red-300' : ''}>{bytes} / {maxBytes} bytes{pending ? ` · ${pending} pending` : ''}</span>
          </div>
        </form>
        {feedback && <p role="status" className="text-xs text-gray-400">{feedback}</p>}
      </div>
      <div className="modal-footer"><p className="text-xs text-gray-500">History stays in this open console. Commands are never sent again automatically.</p></div>
    </div>
  </div>, document.body);
}

function TranscriptEntry({ entry }: { entry: ConsoleEntry }) {
  if (entry.kind !== 'command') return <div className={entry.kind === 'error' ? 'text-red-300' : entry.kind === 'notice' ? 'text-amber-200' : 'text-gray-300'} title={entry.code}>{entry.message}</div>;
  return <div className="my-3 first:mt-0 border-l-2 border-dark-500 pl-3" data-command-status={entry.status}>
    <div className="text-neon-300">{'>'} {entry.line}</div>
    <div className={`font-sans text-[10px] ${entry.status === 'unknown' || entry.status === 'failed' || entry.status === 'not_started' ? 'text-amber-300' : 'text-gray-500'}`} title={entry.code}>{STATUS_LABELS[entry.status]}</div>
    {consoleDisplayRows(entry.lines).map(({ text, indent }, index) => <div key={index}
      className={`console-result-line ${entry.status === 'completed' ? 'text-gray-200' : 'text-amber-200'}`}
      style={{ minHeight: '1lh', overflowWrap: 'anywhere', ...(indent ? {
        paddingInlineStart: `min(${indent}ch, 40%)`, textIndent: `calc(-1 * min(${indent}ch, 40%))`,
      } : {}) }}>{text}</div>)}
    {entry.status === 'failed' && <div className="font-sans text-xs text-amber-300">Execution started and may have produced effects.</div>}
    {entry.status === 'unknown' && <div className="font-sans text-xs text-amber-300">Check the server state before submitting again.</div>}
  </div>;
}
